import type {
  IEventPlan,
  IMirroredPlan,
  TBracketFormat,
  TBracketSize,
  TFillMode,
} from './bracketMath';
import { NCAA_REGION_ORDER } from './bracketMath';

/**
 * The printable-bracket family.
 *
 * ONE engine, one config per route. Every page runs the same builder and the
 * same print pipeline; what differs is the defaults it opens on and the content
 * around it.
 *
 * HOW THE FAMILY IS SPLIT, and how that changed on 2026-09-15.
 *
 * It started as sizes only. The reasoning was that a per-TYPE page would be our
 * third bite at queries we already held twice, counting
 * `/widgets/brackets/double-elimination-bracket` on commoninja.com alongside
 * BracketsNinja's `/types/double-elimination-bracket`.
 *
 * That reasoning was wrong and Sergei was right to push back. Those are two
 * different DOMAINS. Two of our domains ranking for one term is extra shelf
 * space, not self-cannibalisation, and within commoninja.com there is only ever
 * one page per type. `/widgets/brackets/single-elimination-bracket` is in fact
 * a 404, so single elimination was a clean gap the whole time.
 *
 * So the family now splits three ways, all of them real:
 *   - by SIZE    (8 / 16 / 32 / 64) and BLANK, about 10,650 US searches a month
 *                 where our two sites earn between 1 and 246 impressions
 *   - by FORMAT  (single / double / consolation / triple), about 8,440 a month
 *   - by EVENT   (March Madness, NFL playoff, and the rest), still to come
 *
 * ⛔ What is deliberately NOT here: size × format combinations such as
 * `/8-team-double-elimination-bracket`. That is thirty-plus near-identical
 * pages that none of them could earn their own content for, which is the exact
 * thin-content pattern the blog had to be pruned for.
 *
 * ⚠️ Until 2026-09-16 that paragraph ended "every page carries the full size
 * and format switcher, so any combination is one click away". That is no longer
 * true and was the wrong idea anyway: see `lockFormat` on the interface below.
 * A format is now reached by the link grid, which is a link a crawler can also
 * follow, rather than by a control that only exists after JavaScript runs.
 *
 * ⚠️ EVERY PAGE MUST EARN ITS OWN CONTENT. These are not a template with the
 * numbers swapped. Each carries its own rounds-and-matches arithmetic, its own
 * worked example, its own advice and its own questions. If a future variant
 * cannot be given that, it should not be a page.
 *
 * Next up, per the 2026-09-15 decision: round robin (a schedule grid, not a
 * bracket tree, so a new renderer) and consolation. Neither is covered by
 * anything of ours today.
 */

/**
 * The general maker's worked example, i.e. what an untouched
 * `/printable-bracket-maker` is showing.
 *
 * ⚠️ Lives here rather than in `BracketTool` because the bottom CTA banner
 * needs it too. The banner sits below the article, out of reach of the
 * builder's state, so without this it had nothing to hand over on a page
 * nobody had typed into yet. See `CtaBanner`.
 */
export const GENERAL_EXAMPLE_TITLE = 'Office Table Tennis Cup';
export const GENERAL_EXAMPLE_NAMES = [
  'Dana',
  'Marcus',
  'Priya',
  'Tom',
  'Yael',
  'Chen',
  'Sofia',
  'Andre',
];

export interface IVariantSection {
  label: string;
  heading: string;
  intro?: string;
  paras?: string[];
  cards?: { h: string; p: string }[];
  specs?: { h: string; p: string }[];
}

export interface IBracketVariant {
  slug: string;
  /** Shown in the hub, the registry and the sibling strip. */
  name: string;
  shortName: string;
  registryDescription: string;

  defaultSize: TBracketSize;
  defaultFill: TFillMode;
  defaultFormat: TBracketFormat;
  /**
   * Which controls this sheet does NOT offer.
   *
   * ⚠️ Added 2026-09-16 after Sergei reviewed the family. Every page used to
   * carry every control, on the reasoning that landing on the 8-team sheet
   * should never trap anybody at eight. In practice it did the opposite: the
   * double-elimination page opened with the Format switcher showing SINGLE
   * selected, because the switcher rendered before the variant default was
   * read as the visible state. A page whose whole promise is one format should
   * not be offering the other three at all.
   *
   * So a sheet now shows only what belongs to it, and the way to a different
   * format is the link grid, not a control. `lockFormat` is true everywhere,
   * including the general maker, which is single elimination plus the grid.
   */
  lockFormat?: boolean;
  lockSize?: boolean;
  lockFill?: boolean;

  /**
   * A fixed real-world draw, for EVENT variants only.
   *
   * ⚠️ When this is set the draw-order control has nothing to decide and the
   * generic seeding is bypassed entirely. See `IEventPlan` in bracketMath.
   */
  eventPlan?: IEventPlan;

  /**
   * A four-region sheet that meets in the middle, for EVENT variants whose
   * real-world shape the straight left-to-right tree cannot draw.
   *
   * ⚠️ Mutually exclusive with `eventPlan`. A variant sets one or the other,
   * never both: `eventPlan` changes who is drawn against whom, `mirroredPlan`
   * changes the shape of the page. See `IMirroredPlan` in bracketMath.
   */
  mirroredPlan?: IMirroredPlan;

  /** Seeds the players box, so the page is never a blank mystery. */
  exampleTitle: string;
  exampleNames: string[];

  metaTitle: string;
  metaDescription: string;
  h1Lead: string;
  h1Highlight: string;
  heroText: string;
  toolHeading: string;
  toolSubhead: string;

  sections: IVariantSection[];
  faqs: { q: string; a: string }[];
  siblings: string[];
  /** For the WebApplication and HowTo blocks. */
  schemaName: string;
  schemaDescription: string;
}

const BASE = '/free-tools/engagement';

export const variantHref = (slug: string) => `${BASE}/${slug}`;

/** Single elimination: N-1 matches, log2(N) rounds. Used in the copy below. */
function matchesFor(size: number) {
  return size - 1;
}

const NAMES_8 = ['Dana', 'Marcus', 'Priya', 'Tom', 'Yael', 'Chen', 'Sofia', 'Andre'];

const NAMES_16 = [
  ...NAMES_8,
  'Nina',
  'Omar',
  'Ruth',
  'Kwame',
  'Lena',
  'Diego',
  'Aiko',
  'Felix',
];

const NAMES_32 = [
  ...NAMES_16,
  'Bea',
  'Hugo',
  'Iris',
  'Jonas',
  'Kira',
  'Liam',
  'Mira',
  'Noor',
  'Otto',
  'Pilar',
  'Quinn',
  'Rafa',
  'Sana',
  'Theo',
  'Uma',
  'Vik',
];

const NAMES_64 = [
  ...NAMES_32,
  ...NAMES_32.map((n) => `${n} II`),
];

/** Questions every sheet shares. Each variant adds its own on top. */
const COMMON_FAQS = [
  {
    q: 'Can I save the bracket as a PDF?',
    a: 'Yes. Click Print and choose "Save as PDF" as the destination in your browser\'s print dialog, which every major browser has built in on both Windows and Mac. That gives you a true vector PDF that stays sharp at any size. There is also a Download PNG button if you want an image to drop into a chat or a document.',
  },
  {
    q: 'Why are the later rounds blank?',
    a: 'Because that is what a printable bracket is for. Only the first round can be known before anyone plays, so the rest of the sheet prints as empty lines for you to write the winners into as the tournament goes on. If you would rather the bracket advanced winners by itself and updated live on a web page, that is what the Bracket widget does, and you can move your draw into it in one click.',
  },
  {
    q: 'Is my list of players uploaded anywhere?',
    a: 'No. Everything you type stays in your browser and the bracket is drawn on your own device. Nothing is sent to any server, so your players and your tournament name stay private to you.',
  },
  {
    q: 'Is it free?',
    a: 'Yes, completely free. No account, no sign-up, and no credit card. The printed sheet stays clean. The PNG carries a small commoninja.com credit in the corner, and a free account removes it. Make as many brackets as you like.',
  },
];

function sizeVariant({
  size,
  slug,
  names,
  exampleTitle,
  metaTitle,
  metaDescription,
  heroText,
  sections,
  faqs,
  siblings,
}: {
  size: TBracketSize;
  slug: string;
  names: string[];
  exampleTitle: string;
  metaTitle: string;
  metaDescription: string;
  heroText: string;
  sections: IVariantSection[];
  faqs: { q: string; a: string }[];
  siblings: string[];
}): IBracketVariant {
  const rounds = Math.log2(size);
  return {
    slug,
    name: `Free ${size} Team Bracket Maker`,
    shortName: `${size} team`,
    registryDescription: `Print a ${size}-team tournament bracket: ${rounds} rounds, ${matchesFor(
      size
    )} matches, first round seeded from your list.`,
    defaultSize: size,
    defaultFill: 'names',
    defaultFormat: 'single',
    // A size page is ONLY that size, and only single elimination. Offering the
    // other formats here made it a general tool with a number preselected.
    lockSize: true,
    lockFormat: true,
    exampleTitle,
    exampleNames: names.slice(0, size),
    metaTitle,
    metaDescription,
    h1Lead: `Free ${size} Team`,
    h1Highlight: 'Bracket Maker',
    heroText,
    toolHeading: `Build your ${size}-team bracket`,
    toolSubhead: `Round one is seeded from your list of ${size}. Every later round prints as blank lines, so you can write the winners in as they are decided.`,
    sections,
    faqs: [...faqs, ...COMMON_FAQS],
    siblings,
    schemaName: `Free ${size} Team Bracket Maker`,
    schemaDescription: `Free printable ${size}-team tournament bracket generator. Seeds the first round from your player list and prints the remaining ${
      rounds - 1
    } rounds blank to fill in by hand.`,
  };
}

/**
 * The NFL playoff draw, as a fixed slot map.
 *
 * ⚠️ This is NOT a seeded 14-in-16 bracket and must never be replaced by one.
 * `seedOrder(16)` spreads one pool of seeds across the whole tree, so it would
 * interleave AFC and NFC teams and pair sides that cannot meet before the Super
 * Bowl. The NFL is two separate conference draws of seven that converge once.
 *
 * So each half of the sheet is its own 8-slot conference bracket, filled in the
 * standard 8-seed reflection order 1-8, 4-5, 2-7, 3-6. Seed 8 does not exist, so
 * the 1 seed's opponent is a bye: exactly one bye per conference, which is the
 * format as it has stood since the 2020 season.
 *
 * Entrant order is AFC 1..7 then NFC 1..7, which is what `toolSubhead` asks for.
 */
const NFL_PLAN: IEventPlan = {
  // Slot -> entrant index. AFC is entrants 0..6, NFC is entrants 7..13.
  slotPlan: [
    0, null, 3, 4, 1, 6, 2, 5,
    7, null, 10, 11, 8, 13, 9, 12,
  ],
  // Slot -> the seed number printed in the gutter. A bye has none.
  slotSeeds: [
    1, null, 4, 5, 2, 7, 3, 6,
    1, null, 4, 5, 2, 7, 3, 6,
  ],
  roundNames: ['Wild Card', 'Divisional', 'Conference Championship', 'Super Bowl'],
  championLabel: 'Champion',
  halfLabels: ['AFC', 'NFC'],
  halfNoun: 'conference',
};

/**
 * The College Football Playoff draw: twelve teams in a bracket of sixteen.
 *
 * Structurally the opposite of the NFL plan above, which is why both had to be
 * expressible. The CFP is ONE pool of twelve seeded 1 to 12, not two halves, so
 * it carries no `halfLabels` and no `halfNoun`. What it does carry is four byes
 * instead of two: seeds 1 to 4 sit out the first round, and seeds 5 to 12 play
 * 5 v 12, 6 v 11, 7 v 10 and 8 v 9 at the higher seed's home stadium.
 *
 * The quarterfinal pairings are fixed by the CFP itself: 1 plays the 8/9 winner,
 * 4 the 5/12 winner, 2 the 7/10 winner and 3 the 6/11 winner. Laid out top to
 * bottom that is 1, bye, 8, 9 | 4, bye, 5, 12 | 2, bye, 7, 10 | 3, bye, 6, 11,
 * which puts 1 and 4 in one half and 2 and 3 in the other, so the top two seeds
 * can only meet in the National Championship.
 *
 * ⭐ And unlike the NFL, the CFP does NOT reseed between rounds. The bracket
 * published on selection day is the bracket that is played, so this sheet is
 * exactly right all the way to the final rather than right until an upset.
 * Verified against collegefootballplayoff.com's own published format, which
 * states the bracket "will remain in effect throughout the CFP (i.e., no
 * re-seeding)".
 *
 * Entrant order is seeds 1 to 12 in order, which is what `toolSubhead` asks for.
 */
const CFP_PLAN: IEventPlan = {
  // Slot -> entrant index, so entrant 0 is the 1 seed and entrant 11 the 12.
  slotPlan: [
    0, null, 7, 8, 3, null, 4, 11,
    1, null, 6, 9, 2, null, 5, 10,
  ],
  // Slot -> the seed number printed in the gutter. A bye has none.
  slotSeeds: [
    1, null, 8, 9, 4, null, 5, 12,
    2, null, 7, 10, 3, null, 6, 11,
  ],
  roundNames: ['First Round', 'Quarterfinals', 'Semifinals', 'National Championship'],
  championLabel: 'National Champion',
};

/**
 * March Madness: four regions of sixteen that meet in the middle of the page.
 *
 * ⚠️ The region NAMES are fixed on purpose. The committee assigns a region to a
 * host city each year, but the four labels themselves have been stable for a
 * long time and the sheet has to say something down its outside edge or the
 * quarters are anonymous. Anyone running a sixty-four-team draw that is not
 * this tournament wants `64-team-bracket`, which is the same engine with one
 * pool and no captions.
 *
 * The order here is the order entrants are typed: the two regions down the left
 * half of the page first, then the two down the right.
 */
const MARCH_MADNESS_REGIONS: [string, string, string, string] = [
  'South',
  'East',
  'Midwest',
  'West',
];

const MARCH_MADNESS_PLAN: IMirroredPlan = {
  regionLabels: MARCH_MADNESS_REGIONS,
  regionOrder: NCAA_REGION_ORDER,
  roundNames: [
    'First Round',
    'Second Round',
    'Sweet 16',
    'Elite Eight',
    'Final Four',
  ],
  championLabel: 'National Champion',
};

/**
 * The worked example, and it does a second job.
 *
 * ⚠️ Deliberately NOT a set of plausible college names. The one genuinely
 * confusing thing about this page is what order sixty-four teams go in, and a
 * sheet pre-filled with "South 1" through "West 16" answers that by being
 * looked at: the visitor can see that the fifth line typed is the South 5 seed
 * and that it lands beside the 12. Real names would look better and teach
 * nothing. It is also evergreen, which a real field is not.
 */
const NAMES_MARCH_MADNESS = MARCH_MADNESS_REGIONS.flatMap((region) =>
  Array.from({ length: 16 }, (_, i) => `${region} ${i + 1}`)
);

export const VARIANTS: Record<string, IBracketVariant> = {

  'single-elimination-bracket': {
    slug: 'single-elimination-bracket',
    name: 'Free Single Elimination Bracket Maker',
    shortName: 'Single elim',
    registryDescription:
      'Print a single elimination bracket. Lose once and you are out: the shortest format, 4 to 64 players.',
    defaultSize: 16,
    defaultFill: 'names',
    defaultFormat: 'single',
    lockFormat: true,
    exampleTitle: 'Friday Night Knockout',
    exampleNames: NAMES_16,
    metaTitle:
      'Free Single Elimination Bracket Maker: Printable Knockout Template | CommonNinja',
    metaDescription:
      'Make a printable single elimination bracket free. 4 to 64 players, seeded or random draw, print it and fill in the winners by hand. No sign-up.',
    h1Lead: 'Free Single Elimination',
    h1Highlight: 'Bracket Maker',
    heroText:
      'Lose once and you are out. The shortest tournament format there is, the one everybody already understands, and the fastest to run on paper.',
    toolHeading: 'Build your knockout bracket',
    toolSubhead:
      'Round one is seeded from your list. Every later round prints blank, so you write the winners in as they are decided.',
    sections: [
      {
        label: 'The Format',
        heading: 'How single elimination works',
        intro:
          'One loss and your tournament is over. It is the oldest and simplest format, and its arithmetic is unusually tidy.',
        specs: [
          { h: 'One fewer match than players', p: 'Every match knocks exactly one person out, and you finish with one winner. 16 players is always 15 matches, whatever else changes.' },
          { h: 'Rounds double down', p: 'The field halves every round, so 8 players is 3 rounds, 16 is 4, 32 is 5, 64 is 6. Nobody ever plays more than six matches.' },
          { h: 'Byes fill the gaps', p: 'Brackets need a power of two, so 12 players sit in a 16 slot draw with 4 byes. Seed the draw and the byes go to the top seeds, as they should.' },
          { h: 'Shortest to run', p: 'Half the matches of a double elimination draw of the same size. If you have one table and one afternoon, this is the format.' },
        ],
      },
      {
        label: 'The Trade-off',
        heading: 'What single elimination costs you',
        paras: [
          'The weakness is obvious once you have run one: a single bad match ends somebody\u2019s day. The strongest player can go out in round one to a bad draw, a bad call or a bad morning, and the format has no answer to that. In a social tournament it means half your entrants have gone home before the thing has properly started.',
          'If that matters to you, the fix is not a bigger bracket. Consolation gives first-round losers a second draw without doubling the schedule, and double elimination gives everybody a second life at the cost of roughly twice the matches. Both print from this same tool.',
        ],
        cards: [
          { h: 'Use single when', p: 'Time is short, you have one court or table, the result matters less than finishing, or everybody already knows the format.' },
          { h: 'Use something else when', p: 'People have travelled a long way, the ranking has to be genuinely fair, or you want everyone to play more than once.' },
        ],
      },
    ],
    faqs: [
      {
        q: 'What is a single elimination bracket?',
        a: 'A knockout draw where losing one match puts you out of the tournament. Players are paired in round one, the winners advance, and the field halves each round until one is left. It is the format most people picture when they hear the word bracket, and the one used for the knockout stages of almost every major competition.',
      },
      {
        q: 'How many matches are in a single elimination tournament?',
        a: 'Always one fewer than the number of players. 8 players is 7 matches, 16 is 15, 32 is 31, 64 is 63. The reason is simple: each match eliminates exactly one person, and you need to eliminate everyone but the champion. Adding a third place play-off adds one more.',
      },
      {
        q: 'What happens if I do not have a power of two?',
        a: 'The spare slots become byes, which are free passes through the first round. Eleven players go in a 16 slot bracket with five byes. Pick the seeded draw and the byes go to the top five seeds automatically, which is what a real draw does and what players expect.',
      },
      {
        q: 'Is single elimination fair?',
        a: 'It is fair in the sense that everybody faces the same rule, but it is a poor ranking system: it reliably identifies a winner and tells you very little about who was second best. One bad match removes anybody. If you need the result to reflect real strength, double elimination or a round robin will do it better, at the cost of more matches.',
      },
      ...COMMON_FAQS,
    ],
    siblings: ['double-elimination-bracket', 'consolation-bracket', '16-team-bracket'],
    schemaName: 'Free Single Elimination Bracket Maker',
    schemaDescription:
      'Free printable single elimination tournament bracket generator for 4 to 64 players, seeded from your list with later rounds left blank.',
  },

  'double-elimination-bracket': {
    slug: 'double-elimination-bracket',
    name: 'Free Double Elimination Bracket Maker',
    shortName: 'Double elim',
    registryDescription:
      'Print a double elimination bracket with a blank losers draw. Two lives each, 4 to 64 players.',
    defaultSize: 16,
    defaultFill: 'names',
    defaultFormat: 'double',
    lockFormat: true,
    exampleTitle: 'Summer Doubles Cup',
    exampleNames: NAMES_16,
    metaTitle:
      'Free Double Elimination Bracket Maker: Printable Template with Losers Bracket | CommonNinja',
    metaDescription:
      'Make a printable double elimination bracket free. Main draw plus a blank losers bracket, 4 to 64 players. Print both sheets and fill in by hand. No sign-up.',
    h1Lead: 'Free Double Elimination',
    h1Highlight: 'Bracket Maker',
    heroText:
      'Two lives each. A first loss drops you into the losers bracket rather than out of the tournament, so one bad match does not end somebody\u2019s day.',
    toolHeading: 'Build your double elimination bracket',
    toolSubhead:
      'The main draw is seeded from your list and prints on sheet one. The losers bracket prints blank on sheet two, because nobody can know who lands in it.',
    sections: [
      {
        label: 'The Format',
        heading: 'How double elimination works',
        intro:
          'Everyone starts in the main draw. Lose once and you drop into the losers bracket; lose there and you are out.',
        specs: [
          { h: 'Roughly twice the matches', p: 'About 2N minus 2 for N players: 16 players is around 30 matches instead of 15. Budget the day accordingly.' },
          { h: 'Two brackets, two sheets', p: 'The main draw and the losers draw are separate pictures. This tool prints them as two pages so both stay readable.' },
          { h: 'The losers bracket is always blank', p: 'It cannot be seeded, because who drops into it depends on results that have not happened yet. You fill it in as you go.' },
          { h: 'The final is asymmetric', p: 'Whoever comes through the losers bracket arrives with one loss already. Decide in advance whether they must beat the main-draw winner once or twice.' },
        ],
      },
      {
        label: 'Running It',
        heading: 'The two decisions to make before anyone plays',
        cards: [
          {
            h: 'Does the final reset?',
            p: 'The main-draw winner is unbeaten; the losers-bracket winner has one defeat. Many tournaments make the challenger win twice, which is called a bracket reset. Others play one match and call it done. Both are common. Write your choice at the top of the sheet.',
          },
          {
            h: 'How many courts?',
            p: 'Double elimination roughly doubles the fixtures, and the two brackets run in parallel rather than one after the other. Two courts is close to a minimum at sixteen players.',
          },
        ],
        paras: [
          'The reward for all this is a much better ranking. A single elimination draw tells you who won and almost nothing else. Double elimination genuinely separates the top few, because getting to the final through the losers bracket takes a real run of form.',
        ],
      },
    ],
    faqs: [
      {
        q: 'What is a double elimination bracket?',
        a: 'A tournament format where you are only out after losing twice. Everybody starts in the main draw, and a first loss moves you into a second draw called the losers bracket. Win your way through that and you can still reach the final. It is the standard format for pool, cornhole, esports and college baseball, anywhere a single bad match should not end somebody\'s tournament.',
      },
      {
        q: 'How many matches are in a double elimination tournament?',
        a: 'Roughly twice as many as single elimination: about 2N minus 2 for N players, so 16 players is around 30 matches and 32 players is around 62. The exact number depends on whether your final resets. Plan for double the court time of a knockout draw of the same size.',
      },
      {
        q: 'Why is the losers bracket printed blank?',
        a: 'Because it cannot be filled in ahead of time. Who enters the losers bracket, and when, depends entirely on results that have not happened yet, so any printed version would be a guess. The sheet gives you the correct number of rounds and matches for your draw size with every line empty, which is exactly what you need to write into as the day goes on.',
      },
      {
        q: 'Does the losers bracket winner have to beat the champion twice?',
        a: 'That is your call, and you should make it before anyone plays. The argument for is that the main-draw winner is unbeaten and a single loss in the final should not undo that, so the challenger must win two matches. This is called a bracket reset. The argument against is time. Both are widely used, so write your choice on the sheet.',
      },
      ...COMMON_FAQS,
    ],
    siblings: ['single-elimination-bracket', 'consolation-bracket', 'triple-elimination-bracket'],
    schemaName: 'Free Double Elimination Bracket Maker',
    schemaDescription:
      'Free printable double elimination tournament bracket generator. Seeded main draw on one sheet and a blank losers bracket on a second.',
  },

  'consolation-bracket': {
    slug: 'consolation-bracket',
    name: 'Free Consolation Bracket Maker',
    shortName: 'Consolation',
    registryDescription:
      'Print a bracket with a consolation draw, so first-round losers get a second game. 4 to 64 players.',
    defaultSize: 16,
    defaultFill: 'names',
    defaultFormat: 'consolation',
    lockFormat: true,
    exampleTitle: 'Junior Club Tournament',
    exampleNames: NAMES_16,
    metaTitle:
      'Free Consolation Bracket Maker: Printable Template with Consolation Round | CommonNinja',
    metaDescription:
      'Make a printable consolation bracket free. Main draw plus a consolation round for first-round losers, so everyone plays twice. 4 to 64 players, no sign-up.',
    h1Lead: 'Free Consolation',
    h1Highlight: 'Bracket Maker',
    heroText:
      'Nobody drives an hour to play one match and go home. A consolation draw gives the first-round losers a second game, without doubling your schedule.',
    toolHeading: 'Build your consolation bracket',
    toolSubhead:
      'The main draw is seeded from your list. The consolation draw prints blank on a second sheet, half the size, for whoever loses in round one.',
    sections: [
      {
        label: 'The Format',
        heading: 'What a consolation bracket actually is',
        intro:
          'It is the middle ground between single and double elimination, and it is routinely confused with both.',
        specs: [
          { h: 'Only round-one losers', p: 'That is the whole difference from double elimination, where anybody who loses at any stage drops down. Here, lose in round two and you really are finished.' },
          { h: 'Half the size', p: 'A 16-player draw produces 8 first-round losers, so the consolation bracket is an 8-slot knockout.' },
          { h: 'Everybody gets two games', p: 'Which is the point. Nobody travels, pays an entry fee and plays once.' },
          { h: 'Only about half again the matches', p: 'A 16-player draw goes from 15 matches to around 22, not the 30 that double elimination would cost.' },
        ],
      },
      {
        label: 'Why It Exists',
        heading: 'The format schools and youth leagues actually use',
        paras: [
          'Consolation exists for a practical reason rather than a sporting one. If families have driven across a county for a Saturday tournament, a format where half the children play one match and then sit in a hall for four hours is not acceptable, however fair it is. A consolation draw fixes exactly that complaint for about half the extra cost of double elimination.',
          'It is also the honest answer when somebody asks for double elimination but has only budgeted a single day. Offer consolation instead: the guarantee people actually care about is a second game, not a second life.',
        ],
        cards: [
          { h: 'Consolation vs double', p: 'Consolation gives a second game to first-round losers only. Double elimination gives a second life to everybody. The first costs about 50% more matches, the second about 100%.' },
          { h: 'Consolation vs third place', p: 'A third place play-off is one extra match between the losing semi-finalists. A consolation bracket is a whole second draw for everyone knocked out in round one. You can print both.' },
        ],
      },
    ],
    faqs: [
      {
        q: 'What is a consolation bracket?',
        a: 'A second, smaller draw for the players who lose in the first round of the main bracket. It guarantees everybody at least two games without running a full double elimination tournament. It is standard in school, youth and club tournaments, where turning up for a single match is a poor day out.',
      },
      {
        q: 'What is the difference between a consolation bracket and double elimination?',
        a: 'Who gets a second chance. A consolation bracket only takes the first-round losers, so if you lose in round two you are out. Double elimination takes anybody who loses at any point, so everyone has two lives all the way through. Consolation costs about half again as many matches; double elimination costs about twice as many.',
      },
      {
        q: 'How big is the consolation bracket?',
        a: 'Exactly half the main draw, because a first round of N players produces N over 2 losers. A 16 player tournament gives an 8 slot consolation draw, and a 32 player tournament gives a 16 slot one. This tool works that out and prints the right size for you.',
      },
      {
        q: 'Why is the consolation bracket blank?',
        a: 'Because you cannot know who will lose in round one before round one is played. The sheet prints the correct number of slots and rounds with every line empty, ready to fill in with a pen once the first round is done.',
      },
      ...COMMON_FAQS,
    ],
    siblings: ['double-elimination-bracket', 'single-elimination-bracket', '16-team-bracket'],
    schemaName: 'Free Consolation Bracket Maker',
    schemaDescription:
      'Free printable consolation tournament bracket generator. Seeded main draw plus a blank half-size consolation draw for first-round losers.',
  },

  'triple-elimination-bracket': {
    slug: 'triple-elimination-bracket',
    name: 'Free Triple Elimination Bracket Maker',
    shortName: 'Triple elim',
    registryDescription:
      'Print a triple elimination bracket: three losses to be out, with blank second and third chance draws.',
    defaultSize: 8,
    defaultFill: 'names',
    defaultFormat: 'triple',
    lockFormat: true,
    exampleTitle: 'League Finals Weekend',
    exampleNames: NAMES_8,
    metaTitle:
      'Free Triple Elimination Bracket Maker: Printable 3 Loss Tournament Template | CommonNinja',
    metaDescription:
      'Make a printable triple elimination bracket free. Three losses before you are out, with blank second and third chance draws. 4 to 64 players, no sign-up.',
    h1Lead: 'Free Triple Elimination',
    h1Highlight: 'Bracket Maker',
    heroText:
      'Three lives each. Rare, demanding on time, and the right answer when people have travelled a long way and one bad rack should not decide their weekend.',
    toolHeading: 'Build your triple elimination bracket',
    toolSubhead:
      'The main draw is seeded from your list. The second and third chance draws print blank on a second sheet.',
    sections: [
      {
        label: 'The Format',
        heading: 'Three losses, and a warning about time',
        intro:
          'Triple elimination is uncommon for a good reason, and it is worth being honest about that before you print one.',
        specs: [
          { h: 'About 3N matches', p: 'Roughly three times a knockout draw of the same size. Sixteen players is in the region of forty-five matches.' },
          { h: 'Three draws to track', p: 'The main draw, a second chance and a third chance. This is the practical limit of what one person can run on paper.' },
          { h: 'No single standard', p: 'Unlike single and double elimination, there is no universally agreed structure. Agree how re-entries work before you start, and write it on the sheet.' },
          { h: 'Very hard to schedule', p: 'The later draws depend on results from the earlier ones, so you cannot publish times in advance.' },
        ],
      },
      {
        label: 'When It Makes Sense',
        heading: 'Who actually runs triple elimination',
        paras: [
          'It turns up mainly in pool and cornhole leagues, and in small circuits where the same people compete all season. The common thread is that entrants have travelled and paid, the field is small enough to absorb the extra matches, and the result carries ranking points that people care about.',
          'If you are thinking about it for a social tournament, consider double elimination instead. It gives most of the fairness for two-thirds of the matches, and it is a format your players will already understand without a briefing.',
        ],
        cards: [
          { h: 'Good fit', p: 'Small fields, a full weekend, a league where the standings matter, and an organiser who has run one before.' },
          { h: 'Bad fit', p: 'One afternoon, one table, casual entrants, or anybody who needs to know their start time in advance.' },
        ],
      },
    ],
    faqs: [
      {
        q: 'What is a triple elimination bracket?',
        a: 'A tournament format where you are only out after losing three times. It extends double elimination with a third draw, so a player who loses in the main bracket and again in the second chance bracket still has one more life. It is used mainly in pool and cornhole leagues where competitors travel and the standings carry weight across a season.',
      },
      {
        q: 'How many matches are in a triple elimination tournament?',
        a: 'Roughly three times a single elimination draw of the same size, so in the region of 3N matches for N players. Sixteen players works out around forty-five matches against fifteen for a straight knockout. It needs a full day at minimum and realistically a weekend.',
      },
      {
        q: 'Is there a standard triple elimination structure?',
        a: 'No, and that is the main thing to know. Single and double elimination have agreed shapes; triple elimination does not, and different leagues re-enter players at different points. Because of that this sheet prints the main draw seeded and both the second and third chance draws blank, so you can run whichever variant your league uses rather than one we picked for you.',
      },
      {
        q: 'Should I use triple elimination?',
        a: 'Probably not, unless you have run one before. Double elimination gives most of the same fairness for about two-thirds of the matches and everybody already understands it. Triple is worth it when the field is small, the day is long, competitors have travelled, and the result feeds a season-long ranking.',
      },
      ...COMMON_FAQS,
    ],
    siblings: ['double-elimination-bracket', 'consolation-bracket', 'single-elimination-bracket'],
    schemaName: 'Free Triple Elimination Bracket Maker',
    schemaDescription:
      'Free printable triple elimination tournament bracket generator. Seeded main draw with blank second and third chance draws.',
  },
  '8-team-bracket': sizeVariant({
    size: 8,
    slug: '8-team-bracket',
    names: NAMES_8,
    exampleTitle: 'Office Table Tennis Cup',
    metaTitle: 'Free 8 Team Bracket: Printable 8 Person Tournament Template | CommonNinja',
    metaDescription:
      'Make a printable 8 team bracket free. 3 rounds, 7 matches, seeded or random draw. Print it blank and fill in the winners by hand. No sign-up.',
    heroText:
      'Eight players, three rounds, seven matches, and it all fits comfortably on one sheet with room to write. Type your names, print it, and pin it on the wall.',
    sections: [
      {
        label: 'The Shape',
        heading: 'What an 8-team bracket looks like',
        intro:
          'Eight is the friendliest size there is. It is small enough that everybody can read the sheet from across a room, and short enough to finish in an afternoon.',
        specs: [
          { h: '3 rounds', p: 'Quarterfinals, semifinals, final. That is the whole tournament.' },
          { h: '7 matches', p: 'Four, then two, then one. A single elimination draw always needs one fewer match than it has players.' },
          { h: '1 sheet, big text', p: 'Eight rows across a page leaves plenty of height per line, so this is the one size that prints large enough to read from a distance.' },
          { h: '3 wins to take it', p: 'Nobody can be knocked out before the quarterfinals, so every entrant gets at least one match.' },
        ],
      },
      {
        label: 'When To Use It',
        heading: 'Eight is the office tournament size',
        cards: [
          {
            h: 'Table tennis, darts, pool',
            p: 'Seven matches at ten minutes each is a lunchtime. This is why eight is the size most workplace competitions land on without anybody planning it.',
          },
          {
            h: 'Party and family games',
            p: 'Big enough to feel like a real competition, small enough that nobody is sitting out for long. Print two copies so one can live on the fridge.',
          },
        ],
        paras: [
          'If you have nine or ten people, do not force them into eight. Move up to the 16-team sheet and let the extra slots become byes, which the tool works out for you. Cutting two people to make the numbers fit is the one thing that reliably sours a casual tournament.',
        ],
      },
    ],
    faqs: [
      {
        q: 'How many matches are in an 8 team bracket?',
        a: 'Seven. Four quarterfinals, two semifinals and one final. In any single elimination draw the number of matches is always one fewer than the number of players, because every match knocks exactly one person out and you finish with one winner. Add a third place play-off and it becomes eight.',
      },
      {
        q: 'How many rounds does an 8 team bracket have?',
        a: 'Three. The field halves each round: eight become four, four become two, and two play the final. That is why a bracket only works neatly on powers of two, and why seven or six players in an eight-slot bracket produces byes.',
      },
      {
        q: 'What if I only have 6 or 7 players?',
        a: 'Use the 8-team sheet anyway and the leftover slots become byes, which are free passes through the first round. Choose the seeded draw and the byes go to the top seeds automatically, which is how a real tournament handles an uneven field and what your players will expect.',
      },
    ],
    siblings: ['16-team-bracket', '32-team-bracket', 'blank-bracket'],
  }),

  '16-team-bracket': sizeVariant({
    size: 16,
    slug: '16-team-bracket',
    names: NAMES_16,
    exampleTitle: 'Club Championship',
    metaTitle: 'Free 16 Team Bracket: Printable 16 Person Tournament Template | CommonNinja',
    metaDescription:
      'Make a printable 16 team bracket free. 4 rounds, 15 matches, seeded or random draw. Print it and fill in the winners by hand. No sign-up.',
    heroText:
      'Sixteen players, four rounds, fifteen matches. The size where seeding starts to matter, because a bad draw can put your two best players out in round one.',
    sections: [
      {
        label: 'The Shape',
        heading: 'What a 16-team bracket looks like',
        intro:
          'Sixteen is the classic tournament size: big enough to need organising, small enough to run in a day.',
        specs: [
          { h: '4 rounds', p: 'Round of 16, quarterfinals, semifinals, final.' },
          { h: '15 matches', p: 'Eight, four, two, one. Sixteen minus one, as every single elimination draw is.' },
          { h: 'Seeding earns its keep', p: 'With sixteen entrants a random draw can put the two strongest against each other immediately. Seeding puts them in opposite halves.' },
          { h: '4 wins to take it', p: 'A champion plays four matches, so plan the day around four rounds rather than fifteen separate fixtures.' },
        ],
      },
      {
        label: 'Seeding',
        heading: 'Why the draw matters at this size',
        paras: [
          'A seeded draw is not about being fair to the favourites, it is about being fair to everyone watching. If your top two meet in round one, half the field gets an easy path and the final is a let-down. Standard seeding pairs 1 against 16, 8 against 9, 5 against 12 and so on, which keeps the strongest apart for as long as the maths allows.',
          'The tool does this for you when you pick the seeded draw: your list order is the seeding, so the first name you type is seed 1. If you would rather not rank anyone, use the random draw and let the sheet decide.',
        ],
        cards: [
          {
            h: 'Seeded',
            p: 'Your list order is the ranking. Top seeds are kept apart and any byes go to them, exactly as a real draw does.',
          },
          {
            h: 'Random',
            p: 'A fresh shuffle every time you press Re-draw. Fairest when nobody has a track record, and it is the honest option for a social tournament.',
          },
        ],
      },
    ],
    faqs: [
      {
        q: 'How many matches are in a 16 team bracket?',
        a: 'Fifteen. Eight in the round of 16, four quarterfinals, two semifinals and the final. One fewer than the number of players, because each match eliminates exactly one person. A third place play-off makes it sixteen.',
      },
      {
        q: 'How does seeding work in a 16 team bracket?',
        a: 'Standard seeding pairs the top seed with the bottom seed and works inward: 1 against 16, 8 against 9, 5 against 12, 4 against 13, and the same pattern in the lower half. The effect is that seeds 1 and 2 can only meet in the final, and the highest seeds meet the lowest in round one. Pick the seeded draw and the tool arranges this from your list order.',
      },
      {
        q: 'Can I run a 16 team bracket with 12 players?',
        a: 'Yes. Four slots become byes, which are free passes through round one. Under the seeded draw the byes fall to the top four seeds automatically. Your first round then has six real matches instead of eight, and the bracket carries on normally from the quarterfinals.',
      },
    ],
    siblings: ['8-team-bracket', '32-team-bracket', '64-team-bracket'],
  }),

  '32-team-bracket': sizeVariant({
    size: 32,
    slug: '32-team-bracket',
    names: NAMES_32,
    exampleTitle: 'Regional Open',
    metaTitle: 'Free 32 Team Bracket: Printable 32 Person Tournament Template | CommonNinja',
    metaDescription:
      'Make a printable 32 team bracket free. 5 rounds, 31 matches, seeded or random draw, fits one A4 sheet. No sign-up.',
    heroText:
      'Thirty-two players, five rounds, thirty-one matches, still on a single sheet. The size where you should start thinking about how many days the tournament runs.',
    sections: [
      {
        label: 'The Shape',
        heading: 'What a 32-team bracket looks like',
        intro:
          'Thirty-two is where a tournament stops being an afternoon and starts being a schedule.',
        specs: [
          { h: '5 rounds', p: 'Round of 32, round of 16, quarterfinals, semifinals, final.' },
          { h: '31 matches', p: 'Sixteen, eight, four, two, one. That is a lot of court time to plan for.' },
          { h: 'Still one sheet', p: 'Thirty-two rows fit one A4 page in landscape, though the text gets noticeably smaller than the 16-team sheet.' },
          { h: '5 wins to take it', p: 'Worth telling entrants up front, because five rounds in one day is demanding in anything physical.' },
        ],
      },
      {
        label: 'Running It',
        heading: 'Thirty-two takes planning, not just a sheet',
        paras: [
          'At sixteen players you can improvise. At thirty-two you cannot: thirty-one matches on one table is a very long day, so work out how many courts, tables or boards you have and divide. Four tables turns a five-round day into something manageable, because the first round alone is sixteen matches.',
          'Print two copies. One goes on the wall as the master record, one travels with whoever is running the draw. Corrections on a wall chart get messy quickly, and at this size there will be corrections.',
        ],
        cards: [
          {
            h: 'Number your matches',
            p: 'Write a number beside each first-round line so people can find their match without reading thirty-two names. The tool leaves room in the gutter for exactly this.',
          },
          {
            h: 'Decide ties first',
            p: 'Write the tie-break rule at the top of the sheet before anyone plays. Thirty-one matches is thirty-one chances for an argument.',
          },
        ],
      },
    ],
    faqs: [
      {
        q: 'How many matches are in a 32 team bracket?',
        a: 'Thirty-one. Sixteen in the round of 32, eight in the round of 16, four quarterfinals, two semifinals and the final. Always one fewer than the number of players in a single elimination draw. With a third place play-off it is thirty-two.',
      },
      {
        q: 'Does a 32 team bracket fit on one page?',
        a: 'Yes. Thirty-two rows fit one A4 sheet in landscape, and the tool scales the drawing to the page so it can never spill onto a second sheet. The text is smaller than on an 8 or 16-team sheet, so if you are pinning it to a wall for people to read at a distance, print it at A3 from your printer dialog or use two 16-team sheets instead.',
      },
      {
        q: 'How long does a 32 team tournament take?',
        a: 'Thirty-one matches, so it depends entirely on how many can run at once. On a single table at fifteen minutes a match that is nearly eight hours. On four tables the first round takes four sittings rather than sixteen, and a full day becomes realistic. Count your courts before you print.',
      },
    ],
    siblings: ['16-team-bracket', '64-team-bracket', 'blank-bracket'],
  }),

  '64-team-bracket': sizeVariant({
    size: 64,
    slug: '64-team-bracket',
    names: NAMES_64,
    exampleTitle: 'Season Championship',
    metaTitle: 'Free 64 Team Bracket: Printable 64 Person Tournament Template | CommonNinja',
    metaDescription:
      'Make a printable 64 team bracket free. 6 rounds, 63 matches, seeded or random draw, fits one A4 sheet. No sign-up.',
    heroText:
      'Sixty-four players, six rounds, sixty-three matches. The full office-pool shape, on one sheet, with every line ready to write on.',
    sections: [
      {
        label: 'The Shape',
        heading: 'What a 64-team bracket looks like',
        intro:
          'Sixty-four is the biggest draw that still reads as one picture. It is the shape most people picture when they say the word bracket.',
        specs: [
          { h: '6 rounds', p: 'Round of 64, round of 32, round of 16, quarterfinals, semifinals, final.' },
          { h: '63 matches', p: 'Thirty-two, sixteen, eight, four, two, one.' },
          { h: 'Small print, by physics', p: 'Sixty-four rows across an A4 page leaves under 3mm per line, so the names print at roughly 5pt. That is normal for a 64 sheet and it is what the tool reports before you print.' },
          { h: '6 wins to take it', p: 'A champion plays six matches. Nobody plays more than six, however big the field feels.' },
        ],
      },
      {
        label: 'Printing It',
        heading: 'Getting 64 names onto paper you can actually use',
        paras: [
          'The honest constraint at this size is physical, not technical. Sixty-four lines have to share the height of the page, which leaves each one under three millimetres. The tool fits the drawing to the sheet exactly and tells you the resulting text size before you commit paper to it, rather than letting you discover it at the printer.',
          'If five-point text is too small for what you need, you have three good options, and they are all better than squinting.',
        ],
        cards: [
          {
            h: 'Print at A3',
            p: 'Same sheet, double the area, twice the text height. This is what most wall charts do, and any print shop will run one off.',
          },
            {
            h: 'Use the blank sheet',
            p: 'Print the bracket empty and write the names in yourself. Handwriting is smaller and more legible than 5pt type, and you can use the full height of each line.',
          },
          {
            h: 'Split it in two',
            p: 'Run two 32-team sheets, one for each half of the draw, and a third small sheet for the final. Three readable pages instead of one dense one.',
          },
        ],
      },
    ],
    faqs: [
      {
        q: 'How many matches are in a 64 team bracket?',
        a: 'Sixty-three. Thirty-two in the round of 64, sixteen in the round of 32, eight in the round of 16, four quarterfinals, two semifinals and the final. One fewer than the number of teams, because each match eliminates exactly one of them.',
      },
      {
        q: 'How many rounds is a 64 team bracket?',
        a: 'Six. The field halves every round: 64, 32, 16, 8, 4, 2. So a champion wins six matches, and no entrant ever plays more than six however large the draw looks on paper.',
      },
      {
        q: 'Will a 64 team bracket fit on one sheet of A4?',
        a: 'Yes, and the tool guarantees it by fitting the drawing to the page rather than letting it run over. The trade is text size: sixty-four rows leave under three millimetres each, so names print at about five points. The tool shows you that number before you print. If you need it larger, print the same sheet at A3, or print it blank and write the names in by hand.',
      },
      {
        q: 'Is this the same as a March Madness bracket?',
        a: 'The shape is the same, sixty-four entrants over six rounds, and this sheet works perfectly well for a March Madness pool that people fill in with a pen. What it does not do is score predictions or rank a leaderboard, because it is a piece of paper. If you want people to submit picks online and see who is winning, use the March Madness Bracket Maker widget instead, which is also free.',
      },
    ],
    siblings: ['32-team-bracket', '16-team-bracket', 'blank-bracket'],
  }),

  'blank-bracket': {
    slug: 'blank-bracket',
    name: 'Free Blank Bracket Template',
    shortName: 'Blank',
    registryDescription:
      'Print an empty tournament bracket with no names at all. 4 to 64 slots, every line left blank to fill in by hand.',
    defaultSize: 16,
    defaultFill: 'blank',
    defaultFormat: 'single',
    lockFormat: true,
    lockFill: true,
    exampleTitle: '',
    exampleNames: [],
    metaTitle: 'Free Blank Bracket Template: Printable Empty Tournament Bracket | CommonNinja',
    metaDescription:
      'Print a blank tournament bracket free. Empty lines throughout, 4 to 64 slots, ready to write every name in by hand. No sign-up, nothing uploaded.',
    h1Lead: 'Free Blank',
    h1Highlight: 'Bracket Template',
    heroText:
      'An empty bracket with nothing filled in. Pick the number of slots, print it, and write every name in by hand. Nothing to type, no account, and a clean printed sheet.',
    toolHeading: 'Pick your blank bracket',
    toolSubhead:
      'Every line prints empty, including the first round. Choose how many slots you need and write the whole draw in by hand.',
    sections: [
      {
        label: 'Why Blank',
        heading: 'When an empty bracket is the right one',
        intro:
          'Sometimes you do not know who is playing until everyone is in the room. A blank sheet is the honest answer to that, and it has been how tournaments are run on paper for a very long time.',
        cards: [
          {
            h: 'The draw happens live',
            p: 'Names come out of a hat in front of everyone. You cannot type a list in advance, and pretending otherwise takes the fun out of it.',
          },
          {
            h: 'People sign themselves up',
            p: 'Pin it on a noticeboard and let players write their own names into the first round. The sheet is the sign-up sheet.',
          },
          {
            h: 'You want the biggest text',
            p: 'Handwriting fits where 5pt type does not. On a 64-slot sheet, writing the names in is genuinely more legible than printing them.',
          },
          {
            h: 'You need spares',
            p: 'Print ten blanks, keep them in a drawer, and the next tournament costs you nothing but a pen.',
          },
        ],
      },
      {
        label: 'Numbered Lines',
        heading: 'Blank, or numbered and blank',
        paras: [
          'There is a middle option on this page that is easy to miss. "Seed numbers" prints the seeding positions in the gutter (1, 16, 8, 9 and so on) while leaving every line empty. You write the names in, but the sheet has already worked out who should face whom.',
          'That is the version to use when you know how many people are coming and roughly how good they are, but not their names. It gives you a proper seeded draw without typing a list.',
        ],
        specs: [
          { h: 'Blank', p: 'Nothing printed at all. Every line empty, including the first round.' },
          { h: 'Seed numbers', p: 'Positions numbered, lines empty. A correct seeded draw you fill in by hand.' },
          { h: 'Names', p: 'Type or paste a list and the first round is filled in for you.' },
        ],
      },
      {
        label: 'Sizes',
        heading: 'How many slots do you need?',
        paras: [
          'Brackets only work neatly on powers of two, so pick the smallest size that holds everyone: 4, 8, 16, 32 or 64. If your numbers fall in between, go up rather than down. The empty slots become byes, which are free passes through the first round, and that is much better than turning people away.',
          'Eleven players go on a 16-slot sheet with five byes. Forty go on a 64-slot sheet with twenty-four. It looks lopsided written down and it plays perfectly normally.',
        ],
      },
    ],
    faqs: [
      {
        q: 'What is a blank bracket?',
        a: 'A tournament bracket printed with all its lines empty, so you can write the names in by hand. The structure is there, the rounds and the connections between matches, but nothing is filled in. It is what you want when the draw happens in the room, when people sign themselves up, or when you simply want a stack of spares in a drawer.',
      },
      {
        q: 'How many slots can a blank bracket have?',
        a: 'Four, eight, sixteen, thirty-two or sixty-four. Those are the sizes a single elimination bracket works on, because the field has to halve cleanly every round. Pick the smallest one that holds everybody and let the spare slots be byes.',
      },
      {
        q: 'Can I print a blank double elimination bracket?',
        a: 'Yes, on the double elimination page. That sheet adds a losers bracket with the right number of rounds for your size, printed on its own page, and every line of it is empty, which is correct: nobody can know who drops into a losers bracket before the tournament starts. Set the fill to Blank there and the main draw prints empty too.',
      },
      {
        q: 'What is the difference between blank and seed numbers?',
        a: 'Blank prints nothing at all. Seed numbers prints the seeding positions beside each empty line, so the sheet has already paired seed 1 against the bottom seed, 8 against 9 and so on, and you just write the names in. Use seed numbers when you know how strong people are but not who is coming.',
      },
    ],
    siblings: ['8-team-bracket', '16-team-bracket', '64-team-bracket'],
    schemaName: 'Free Blank Bracket Template',
    schemaDescription:
      'Free printable blank tournament bracket. Empty lines throughout, 4 to 64 slots, ready to fill in by hand.',
  },
  /**
   * The first EVENT sheet, and the first draw the generic engine could not
   * express. See `NFL_PLAN` above for why.
   *
   * ⚠️ Deliberately EVERGREEN. No year in the slug, the title or the copy, and
   * the example draw is seed placeholders rather than this season's teams. A
   * page that names a season is wrong from the day the season ends, and we
   * already learned that the expensive way on the countdown templates, 20 of
   * which shipped with dates that had passed. The visitor types the real teams
   * in once the field is set, which is also the only honest thing to do on a
   * page that goes live in October.
   */
  'nfl-playoff-bracket': {
    slug: 'nfl-playoff-bracket',
    name: 'Free Printable NFL Playoff Bracket',
    shortName: 'NFL playoff',
    registryDescription:
      'Print the NFL playoff bracket: 14 teams, both conferences, Wild Card through to the Super Bowl, with the two byes already in place.',
    defaultSize: 16,
    defaultFill: 'names',
    defaultFormat: 'single',
    // The NFL draw IS the page. A size or format control here would only offer
    // ways to stop it being an NFL bracket.
    lockFormat: true,
    lockSize: true,
    lockFill: true,
    eventPlan: NFL_PLAN,
    exampleTitle: 'NFL Playoffs',
    exampleNames: [
      'AFC 1 seed',
      'AFC 2 seed',
      'AFC 3 seed',
      'AFC 4 seed',
      'AFC 5 seed',
      'AFC 6 seed',
      'AFC 7 seed',
      'NFC 1 seed',
      'NFC 2 seed',
      'NFC 3 seed',
      'NFC 4 seed',
      'NFC 5 seed',
      'NFC 6 seed',
      'NFC 7 seed',
    ],
    metaTitle: 'Free Printable NFL Playoff Bracket | CommonNinja',
    metaDescription:
      'Print a blank NFL playoff bracket free. All 14 teams, AFC and NFC, Wild Card round through to the Super Bowl, with both first-round byes already drawn in.',
    h1Lead: 'Free Printable',
    h1Highlight: 'NFL Playoff Bracket',
    heroText:
      'The full playoff picture on one sheet: seven teams in each conference, the two byes already in place, and every round from Wild Card weekend to the Super Bowl. Type this season’s teams in, or print it empty and fill it in as the games are played.',
    toolHeading: 'Build your NFL playoff bracket',
    toolSubhead:
      'Fourteen lines, AFC seeds 1 to 7 then NFC seeds 1 to 7, in that order. The sheet handles the rest: each conference keeps to its own half, the top seeds get their byes, and the two halves only meet in the Super Bowl.',
    sections: [
      {
        label: 'The Format',
        heading: 'How the NFL playoff bracket is built',
        intro:
          'Fourteen teams make the playoffs, seven from each conference: the four division winners, seeded 1 to 4, and three wild cards seeded 5 to 7. Only the number one seed in each conference gets a first-round bye. That is why this sheet has sixteen lines and two of them are byes.',
        specs: [
          {
            h: 'Wild Card',
            p: '6 games. Seeds 2 v 7, 3 v 6 and 4 v 5 in each conference. The 1 seeds sit it out.',
          },
          {
            h: 'Divisional',
            p: '4 games. The rested 1 seeds come in against the surviving wild card teams.',
          },
          {
            h: 'Conference Championship',
            p: '2 games. One AFC, one NFC. The winners are the two Super Bowl teams.',
          },
          {
            h: 'Super Bowl',
            p: '1 game, on neutral ground. Thirteen games in all, across four weekends.',
          },
        ],
      },
      {
        label: 'One Catch',
        heading: 'The one thing a printed bracket cannot do',
        paras: [
          'The NFL reseeds every round. After Wild Card weekend the surviving number one seed is matched against the LOWEST remaining seed in its conference, not against whoever happens to be next on the page. So if the 7 seed pulls off an upset, the real Divisional round sends them to the 1 seed, while a fixed sheet like this one sends them onward to whoever won the 3 v 6 game.',
          'Every printable bracket has this, because paper cannot rearrange itself. It only bites when a lower seed wins, and it never affects the Wild Card round, the Conference Championships or the Super Bowl. If it happens, draw an arrow. That is what the pen is for, and it is the part of the weekend everyone argues about anyway.',
          'If you would rather the bracket advanced winners by itself and stayed right on a web page all season, that is what the Bracket widget does, and you can move this draw straight into it in one click.',
        ],
      },
      {
        label: 'Office Pool',
        heading: 'Running a playoff pool off the sheet',
        intro:
          'A printed bracket is still the simplest way to run a pool, and it does not need an app or anybody’s email address. Hand out a copy each, collect them before the first kickoff, and score them as the weekends go.',
        cards: [
          {
            h: 'Weight the later rounds',
            p: 'A common scheme is 1 point per Wild Card pick, 2 for Divisional, 4 for the Conference Championships and 8 for the Super Bowl. It keeps everyone alive into February instead of settling it on the first weekend.',
          },
          {
            h: 'Collect them before kickoff',
            p: 'Every entry has to be in before the first Wild Card game starts. Print a few spares: somebody always wants to redo theirs.',
          },
          {
            h: 'Ask for a tiebreaker',
            p: 'Have everyone write a guess at the total points in the Super Bowl somewhere on the sheet. Closest without going over settles a tie, and ties are common.',
          },
          {
            h: 'Pin the master copy up',
            p: 'Keep one sheet as the official record and fill it in as results come in, so nobody has to take anybody’s word for what the score was.',
          },
        ],
      },
      {
        label: 'Printing',
        heading: 'Getting it onto paper cleanly',
        paras: [
          'The sheet is drawn to landscape and fits on one page, with the AFC in the top half and the NFC in the bottom. Press Print and choose "Save as PDF" as the destination if you want to send it round rather than print it, or use Download PNG for something to drop into a group chat.',
          'Nothing you type is uploaded. The bracket is drawn on your own device, so your pool stays your own business. The printed sheet stays clean, and the PNG carries a small commoninja.com credit that a free account removes.',
        ],
      },
    ],
    faqs: [
      {
        q: 'How many teams make the NFL playoffs?',
        a: 'Fourteen, seven from each conference. Each conference sends its four division winners, seeded 1 to 4 by record, and three wild card teams seeded 5 to 7. That has been the format since the 2020 season, when the field grew from twelve to fourteen.',
      },
      {
        q: 'Which teams get a bye in the NFL playoffs?',
        a: 'Only the number one seed in each conference, so two byes in total. Before the 2020 season both the 1 and 2 seeds were rested, which is why older printable brackets have four byes and look different from this one. The top seed also hosts every game it plays until the Super Bowl.',
      },
      {
        q: 'Why does my bracket not match the real Divisional matchups?',
        a: 'Because the NFL reseeds after the Wild Card round: the surviving top seed plays the lowest remaining seed in its conference. A printed bracket is a fixed tree, so it cannot move a team across the page. It only differs when a lower seed wins, and the fix is to draw an arrow to where that team actually goes. Every paper bracket works this way.',
      },
      {
        q: 'How many games are in the NFL playoffs?',
        a: 'Thirteen. Six on Wild Card weekend, four in the Divisional round, two Conference Championship games and the Super Bowl. Fourteen teams, thirteen games, one champion.',
      },
      {
        q: 'What order do I type the teams in?',
        a: 'AFC seeds 1 through 7 first, then NFC seeds 1 through 7, fourteen lines in all. The sheet puts each conference in its own half and gives the two top seeds their byes, so once the order is right everything else is drawn for you.',
      },
      {
        q: 'Can I print it before the playoff teams are decided?',
        a: 'Yes, and that is what the seed placeholders are for. Leave the lines as "AFC 1 seed", "NFC 4 seed" and so on, print it, and write the real teams in as the regular season settles them. The structure never changes, only the names do.',
      },
      {
        q: 'Is this an official NFL bracket?',
        a: 'No. CommonNinja is not affiliated with, endorsed by or sponsored by the National Football League. This is a free printable sheet laid out to match the published playoff format, for personal use, pools and wall charts.',
      },
    ],
    // ⚠️ Every slug here must be a VARIANTS key. `printable-bracket-maker` is
    // NOT one: the general maker has its own page and is not in the record, so
    // listing it here threw on `sib.slug` and 500'd the route.
    siblings: ['16-team-bracket', 'blank-bracket', 'single-elimination-bracket'],
    schemaName: 'Free Printable NFL Playoff Bracket',
    schemaDescription:
      'Free printable NFL playoff bracket. All 14 teams across both conferences, Wild Card round through to the Super Bowl, with both first-round byes drawn in.',
  },
  /**
   * The second EVENT sheet, and the first one with no halves to it.
   *
   * ⚠️ Same evergreen discipline as the NFL page: no year in the slug, the
   * title or the copy, and the example draw is seed placeholders rather than a
   * season's teams. A page called "2026 bracket" is wrong from January.
   *
   * Every format fact here was checked against collegefootballplayoff.com's own
   * published format page rather than against a news write-up, because the
   * automatic-bid rules have changed twice in three seasons while the shape of
   * the bracket has not. The copy leans on the shape and keeps the bid rules to
   * one line that says plainly they move.
   */
  'college-football-playoff-bracket': {
    slug: 'college-football-playoff-bracket',
    name: 'Free Printable College Football Playoff Bracket',
    shortName: 'CFP',
    registryDescription:
      'Print the College Football Playoff bracket: 12 teams, the four first-round byes, first round through to the National Championship.',
    defaultSize: 16,
    defaultFill: 'names',
    defaultFormat: 'single',
    // The CFP draw IS the page, exactly as on the NFL sheet. A size or format
    // control here would only offer ways to stop it being a CFP bracket.
    lockFormat: true,
    lockSize: true,
    lockFill: true,
    eventPlan: CFP_PLAN,
    exampleTitle: 'College Football Playoff',
    exampleNames: [
      '1 seed',
      '2 seed',
      '3 seed',
      '4 seed',
      '5 seed',
      '6 seed',
      '7 seed',
      '8 seed',
      '9 seed',
      '10 seed',
      '11 seed',
      '12 seed',
    ],
    metaTitle: 'Free Printable College Football Playoff Bracket | CommonNinja',
    metaDescription:
      'Print a blank College Football Playoff bracket free. All 12 teams, the four first-round byes drawn in, first round through to the National Championship.',
    h1Lead: 'Free Printable',
    h1Highlight: 'College Football Playoff Bracket',
    heroText:
      'The whole twelve-team field on one sheet: seeds 1 to 4 resting through the first round, seeds 5 to 12 paired the way the Playoff pairs them, and every round from the campus games to the National Championship. Type this season’s teams in, or print it empty and fill it in as the games are played.',
    toolHeading: 'Build your College Football Playoff bracket',
    toolSubhead:
      'Twelve lines, seeds 1 to 12 in order. The sheet handles the rest: the top four get their byes, the other eight are paired 5 v 12, 6 v 11, 7 v 10 and 8 v 9, and each first-round winner lands in the right quarterfinal.',
    sections: [
      {
        label: 'The Format',
        heading: 'How the College Football Playoff bracket is built',
        intro:
          'Twelve teams, five automatic bids and seven at-large places, all of them seeded 1 to 12 by the selection committee’s final rankings. The top four seeds rest while the other eight play, which is why this sheet has sixteen lines and four of them are byes. Eleven games in all.',
        specs: [
          {
            h: 'First Round',
            p: '4 games, played on campus at the higher seed’s home stadium. 5 v 12, 6 v 11, 7 v 10 and 8 v 9. Seeds 1 to 4 sit it out.',
          },
          {
            h: 'Quarterfinals',
            p: '4 games at bowl sites. The rested top four come in: 1 against the 8/9 winner, 4 against the 5/12 winner, 2 against the 7/10 winner and 3 against the 6/11 winner.',
          },
          {
            h: 'Semifinals',
            p: '2 games, again at bowl sites. One half of the draw sends a team, the other half sends the other.',
          },
          {
            h: 'National Championship',
            p: '1 game at a neutral stadium, a week or so later. Twelve teams, eleven games, one champion.',
          },
        ],
      },
      {
        label: 'No Reseeding',
        heading: 'Why this sheet is still right in January',
        paras: [
          'Most printed brackets carry a quiet caveat: the real competition rearranges itself and the paper cannot. The NFL, for one, reseeds after its first weekend, so the surviving top seed is given the lowest remaining seed rather than whoever is next on the page.',
          'The College Football Playoff does not do that. The bracket published on selection day is the bracket that gets played, with no reseeding between rounds and no reshuffling to avoid a rematch or to keep two teams from the same conference apart. Whoever wins the 8 v 9 game plays the 1 seed, whatever happens elsewhere.',
          'So a printed CFP sheet is not an approximation. Fill the twelve seeds in once the field is announced and every line on it will be correct through to the title game, which is exactly what you want from something pinned to a wall for six weeks.',
        ],
      },
      {
        label: 'Pool Rules',
        heading: 'Running a playoff pool off the sheet',
        intro:
          'A printed bracket is still the easiest way to run a pool, and it needs no app and nobody’s email address. Hand out a copy each, collect them before the first game kicks off, and score them as the rounds go.',
        cards: [
          {
            h: 'Weight the later rounds',
            p: 'A common scheme is 1 point per first-round pick, 2 per quarterfinal, 4 per semifinal and 8 for the champion. Eleven games is a short pool, so front-loaded scoring settles it far too early.',
          },
          {
            h: 'Collect them before the first kickoff',
            p: 'Every entry has to be in before the opening campus game. Print a few spares: somebody always wants to redo theirs once they have seen the seeding.',
          },
          {
            h: 'Ask for a tiebreaker',
            p: 'Have everyone write a guess at the total points in the National Championship game somewhere on the sheet. Closest without going over settles a tie, and with only eleven games ties are common.',
          },
          {
            h: 'Pin the master copy up',
            p: 'Keep one sheet as the official record and fill it in as results come in, so nobody has to take anybody’s word for what happened in a bowl game three weeks ago.',
          },
        ],
      },
      {
        label: 'Before Selection Day',
        heading: 'Printing it before the field is announced',
        paras: [
          'You do not have to wait for selection day. Leave the lines as "1 seed", "5 seed" and so on, print it, and write the real teams in when the committee publishes the final rankings. The structure never changes from season to season, only the names do.',
          'That also makes it a usable sheet through November. Pencil in who you think the twelve will be, argue about it, and rub it out each time the rankings come out. It is a better conversation than a list, because the sheet shows you immediately who would have to beat whom.',
        ],
      },
      {
        label: 'Printing',
        heading: 'Getting it onto paper cleanly',
        paras: [
          'The sheet is drawn to landscape and fits on one page. Press Print and choose "Save as PDF" as the destination if you want to send it round rather than print it, or use Download PNG for something to drop into a group chat.',
          'Nothing you type is uploaded. The bracket is drawn on your own device, so your pool stays your own business. The printed sheet stays clean, and the PNG carries a small commoninja.com credit that a free account removes.',
        ],
      },
    ],
    faqs: [
      {
        q: 'How many teams make the College Football Playoff?',
        a: 'Twelve. Five places are automatic bids and the other seven are at-large selections, chosen by the selection committee. The field grew from four to twelve for the 2024 season. The rules for who gets the automatic bids have already been changed more than once, so check the current season’s version if it matters to you, but the bracket itself has been the same twelve-team shape throughout.',
      },
      {
        q: 'Which teams get a first-round bye in the College Football Playoff?',
        a: 'Seeds 1, 2, 3 and 4, so four byes in total. Since the 2025 season the seeding is straight off the committee’s final rankings, so the four byes go to the four highest-ranked teams whether or not they won a conference. Before that the top four seeds were reserved for the highest-ranked conference champions, which is why older printable brackets pair the seeds differently.',
      },
      {
        q: 'What are the first-round matchups?',
        a: '5 v 12, 6 v 11, 7 v 10 and 8 v 9, and all four are played at the higher seed’s home stadium rather than at a bowl site. The winners go on to meet the rested top four in the quarterfinals: the 8/9 winner plays the 1 seed, the 5/12 winner plays the 4, the 7/10 winner plays the 2 and the 6/11 winner plays the 3.',
      },
      {
        q: 'Does the College Football Playoff reseed after the first round?',
        a: 'No. The bracket is set on selection day and stays in effect for the whole Playoff, with no reseeding and no changes made to avoid rematches or to separate teams from the same conference. That is the difference between this sheet and a printed NFL bracket, which does go out of date the moment a lower seed wins, and it means every line here holds through to the National Championship.',
      },
      {
        q: 'How many games are in the College Football Playoff?',
        a: 'Eleven. Four first-round games on campus, four quarterfinals, two semifinals and the National Championship. The quarterfinals and semifinals are played at bowl sites, and the title game is at a neutral stadium chosen years in advance.',
      },
      {
        q: 'What order do I type the teams in?',
        a: 'Seeds 1 through 12, in order, twelve lines in all. The sheet gives the top four their byes, pairs the other eight the way the Playoff pairs them and sends each first-round winner into the right quarterfinal, so once the order is right everything else is drawn for you.',
      },
      {
        q: 'Can I print it before the playoff field is announced?',
        a: 'Yes, and that is what the seed placeholders are for. Leave the lines as "1 seed", "4 seed" and so on, print it, and write the real teams in on selection day. Nothing about the layout depends on who is in it.',
      },
      {
        q: 'Is this an official College Football Playoff bracket?',
        a: 'No. CommonNinja is not affiliated with, endorsed by or sponsored by the College Football Playoff, the NCAA or any conference. This is a free printable sheet laid out to match the published playoff format, for personal use, pools and wall charts.',
      },
    ],
    // ⚠️ Every slug here must be a VARIANTS key. `printable-bracket-maker` is
    // NOT one: see the note on the NFL variant above.
    siblings: ['nfl-playoff-bracket', '16-team-bracket', 'blank-bracket'],
    schemaName: 'Free Printable College Football Playoff Bracket',
    schemaDescription:
      'Free printable College Football Playoff bracket. All 12 teams, the four first-round byes drawn in, first round through to the National Championship.',
  },

  'march-madness-bracket': {
    slug: 'march-madness-bracket',
    name: 'Free Printable March Madness Bracket',
    shortName: 'March Madness',
    registryDescription:
      'Print the March Madness bracket: 64 teams, four regions of 16, First Round through to the national title on one sheet.',
    defaultSize: 64,
    defaultFill: 'names',
    defaultFormat: 'single',
    lockSize: true,
    lockFormat: true,
    mirroredPlan: MARCH_MADNESS_PLAN,
    exampleTitle: 'Office Pool',
    exampleNames: NAMES_MARCH_MADNESS,
    metaTitle:
      'Free Printable March Madness Bracket: 64-Team Sheet | CommonNinja',
    metaDescription:
      'Free printable March Madness bracket. All 64 teams across four regions, First Round to the national title, on one sheet. Type teams in or print it blank.',
    h1Lead: 'Free Printable March Madness',
    h1Highlight: 'Bracket',
    heroText:
      'The full field on one page, drawn the way it is meant to be: four regions of sixteen, two running in from each side, and the national title line in the middle. Type the teams in, or print it empty and fill it in as the games are played.',
    toolHeading: 'Build your March Madness bracket',
    toolSubhead:
      'Sixty-four lines, one region at a time, each region in seed order from the 1 seed down to the 16. The sheet handles the rest: it puts each region in its own quarter of the page and pairs the seeds the way the bracket does.',

    sections: [
      {
        label: 'The sheet',
        heading: 'Why this bracket is drawn from both sides',
        intro:
          'Every other sheet in this family runs left to right. This one does not, because the tournament does not.',
        paras: [
          'The field is four regions of sixteen. Each region plays itself down to one team, and only then do the four survivors meet. Drawn as a single row of sixty-four lines that is still arithmetically correct, but it is not the sheet anybody recognises, and it wastes the page: sixty-four rows stacked down one side of a landscape sheet squeeze the text to about five points.',
          'So two regions run in from the left, two run in from the right, and the four regional champions converge on one line in the middle. Each side carries thirty-two rows instead of sixty-four, which roughly doubles the printed text size on the same piece of paper, and the shape matches the bracket on the wall of every office in the country.',
        ],
        specs: [
          {
            h: 'Four regions, sixteen each',
            p: 'Two down the left half of the page, two down the right. Each is captioned down the outside edge, so nothing on the sheet is anonymous.',
          },
          {
            h: 'Six rounds, 63 games',
            p: '32 first-round games, then 16, 8, 4, 2 and the final. Every round halves the field, which is why the total is always one less than the number of teams.',
          },
          {
            h: 'One page',
            p: 'A4 or Letter, landscape, fitted so it cannot spill onto a second sheet. The tool tells you the printed text size before you commit paper to it.',
          },
        ],
      },
      {
        label: 'Seeding',
        heading: 'How the seeds are paired inside a region',
        intro:
          'Type each region from the 1 seed down to the 16 and the sheet does the pairing. It is worth knowing what it is doing.',
        paras: [
          'Inside a region the lines read 1 against 16, 8 against 9, 5 against 12, 4 against 13, 6 against 11, 3 against 14, 7 against 10, and 2 against 15. The logic is that the strongest team should meet the weakest, and that the 1 and the 2 seed should not be able to meet before the regional final.',
          'That order is not the one a general bracket generator produces. Build a sixteen-team draw by the usual method of repeated reflection and you get 1-16, 8-9, 4-13, 5-12 instead, which is a perfectly legal seeding and the wrong sheet. The 5 against 12 game sits where it does because this bracket puts it there, and that placement is the reason everyone has an opinion about it.',
        ],
      },
      {
        label: 'The play-in games',
        heading: 'Sixty-eight teams, sixty-four lines',
        paras: [
          'The tournament invites sixty-eight teams. Four play-in games cut that to sixty-four before the first round proper, and the four winners take four of the places on this sheet. Which lines they land on moves from year to year, so the sheet leaves them as ordinary lines rather than pretending to know.',
          'If you are filling this in before those games are played, write the two candidates on the line with a slash between them and cross one out later. That is what the extra width on each line is for.',
        ],
      },
      {
        label: 'Pools',
        heading: 'Running a paper pool that does not fall apart',
        intro:
          'A bracket pool is a scoring system with a bracket attached. Decide the scoring before you hand out the sheets, not after.',
        cards: [
          {
            h: 'Double the points each round',
            p: 'One point per correct first-round pick, then 2, 4, 8, 16 and 32. It totals 192 points and it is the most common scheme, because it keeps a pool alive into the second week instead of settling it on the first Thursday.',
          },
          {
            h: 'Print one blank per person',
            p: 'Switch the fill to Blank and print as many as you need. Everyone writes the same sixty-four names in themselves, which is slower but stops one person quietly editing the field.',
          },
          {
            h: 'Collect before the first game',
            p: 'Set a deadline at the tip-off of the first game and hold it. A sheet handed in after a result is known is not a prediction.',
          },
          {
            h: 'Decide the tie-break up front',
            p: 'The usual one is the combined score of the final. Write it on the sheet in the title box so nobody argues about it in the last five minutes.',
          },
        ],
      },
    ],

    faqs: [
      {
        q: 'How many teams are on this bracket?',
        a: 'Sixty-four, in four regions of sixteen. The tournament itself invites sixty-eight teams and plays four games to get down to sixty-four, so this sheet is the bracket as it stands once those are done. If you are filling it in beforehand, put both candidates on the line and cross one out later.',
      },
      {
        q: 'Why do the two halves of the sheet face each other?',
        a: 'Because that is the shape of the tournament and of every bracket people fill in. Two regions run in from the left, two from the right, and the four regional champions meet in the middle. It also prints far better than a single row of sixty-four lines would: each side carries thirty-two rows instead of sixty-four, so the text comes out roughly twice the size on the same page.',
      },
      {
        q: 'What order do I type the teams in?',
        a: 'One region at a time, and each region in seed order: the 1 seed first, then the 2, all the way down to the 16. Do that for all four regions and you have sixty-four lines. The sheet takes care of where each seed actually goes, so you never have to know that the 8 seed belongs on the third line.',
      },
      {
        q: 'Are the seeds paired the way the real bracket pairs them?',
        a: 'Yes. Inside each region the sheet reads 1 against 16, 8 against 9, 5 against 12, 4 against 13, 6 against 11, 3 against 14, 7 against 10, and 2 against 15, which is the bracket order rather than the order a general seeding algorithm produces. That is why the 5 against 12 game sits where you expect it to.',
      },
      {
        q: 'Can I print it blank and fill it in by hand?',
        a: 'Yes, and for a pool that is usually what you want. Set the fill to Blank and every line prints empty, seed numbers included, so you can hand out identical sheets and let people write the field in themselves. Set it to Seeds instead and the seed numbers print but the names do not.',
      },
      {
        q: 'Is there a third-place game on this sheet?',
        a: 'No. The tournament does not play one, so the sheet does not draw one. The two losing teams in the Final Four are finished, and the only line after the semifinals is the national title.',
      },
      {
        q: 'Why is there no year on this page?',
        a: 'Because the bracket is the same shape every year and a page with a year on it is out of date the moment the next one starts. The field changes, the structure does not, so this sheet works for whichever tournament you are printing it for.',
      },
    ],

    siblings: ['64-team-bracket', 'blank-bracket', 'nfl-playoff-bracket'],
    schemaName: 'Free Printable March Madness Bracket',
    schemaDescription:
      'Free printable March Madness bracket generator. All 64 teams across four regions of 16, seeded in bracket order, from the first round through to the national title on one landscape sheet.',
  },
};

export const VARIANT_SLUGS = Object.keys(VARIANTS);

/**
 * The four formats, as LINKS rather than as a control.
 *
 * Replaces the Format switcher that used to sit in the panel. Sergei's call on
 * 2026-09-16, and it is the better structure for two separate reasons:
 *
 *  - On screen: a switcher on the double-elimination page offering "Single"
 *    invites you to turn the page into the thing it is not. Each of these is a
 *    real page with its own worked example, its own arithmetic and its own
 *    questions, so sending people to it is strictly better than mutating the
 *    sheet in place.
 *  - For search: a `<Link>` is crawlable and passes weight between the four
 *    pages. A `useState` toggle is invisible to a crawler, so the family used
 *    to have no internal linking between its own formats at all.
 *
 * `blurb` is the difference in one line, because the names alone do not tell
 * anybody which one they want.
 */
export const FORMAT_LINKS: {
  slug: string;
  label: string;
  blurb: string;
  sheets: string;
}[] = [
  {
    slug: 'single-elimination-bracket',
    label: 'Single elimination',
    blurb: 'Lose once and you are out. The shortest format, and the one most people already know.',
    sheets: '1 sheet',
  },
  {
    slug: 'double-elimination-bracket',
    label: 'Double elimination',
    blurb: 'A first loss drops you into a losers bracket. Everybody has to be beaten twice.',
    sheets: '2 sheets',
  },
  {
    slug: 'consolation-bracket',
    label: 'Consolation',
    blurb: 'Round-one losers play on in a draw half the size, so nobody goes home after one game.',
    sheets: '2 sheets',
  },
  {
    slug: 'triple-elimination-bracket',
    label: 'Triple elimination',
    blurb: 'Three losses to be out. Adds second and third chance draws, both blank.',
    sheets: '2 sheets',
  },
];

/**
 * The size sheets and the blank one, as the grid's second row.
 *
 * Added 2026-09-16: the grid shipped with the four formats only, which left the
 * five size pages reachable from some pages and not others. All ten are now one
 * click from all ten, which is both the navigation Sergei asked for and the
 * internal linking that keeps ten pages on one topic from competing.
 */
export const SIZE_LINKS: {
  slug: string;
  label: string;
  blurb: string;
  sheets: string;
}[] = [
  {
    slug: '8-team-bracket',
    label: '8 teams',
    blurb: 'Three rounds, seven matches. Round one is the quarterfinals.',
    sheets: '8 slots',
  },
  {
    slug: '16-team-bracket',
    label: '16 teams',
    blurb: 'Four rounds, fifteen matches. The most common size of all.',
    sheets: '16 slots',
  },
  {
    slug: '32-team-bracket',
    label: '32 teams',
    blurb: 'Five rounds, thirty-one matches, still one sheet of paper.',
    sheets: '32 slots',
  },
  {
    slug: '64-team-bracket',
    label: '64 teams',
    blurb: 'Six rounds, sixty-three matches. The largest a page can hold and stay readable.',
    sheets: '64 slots',
  },
  {
    slug: 'blank-bracket',
    label: 'Blank sheet',
    blurb: 'No names at all, every line empty, ready to fill in by hand.',
    sheets: 'Any size',
  },
];

/**
 * The event sheets: a real tournament's own draw rather than a generic tree.
 *
 * A third row on the same grid, for the same two reasons the other two exist.
 * On screen it is the only way somebody on the 16-team page finds out we have
 * the actual NFL layout; for search it is a crawlable link, so a new event page
 * is not an orphan on the day it ships.
 */
export const EVENT_LINKS: {
  slug: string;
  label: string;
  blurb: string;
  sheets: string;
}[] = [
  {
    slug: 'nfl-playoff-bracket',
    label: 'NFL playoffs',
    blurb: 'Both conferences, the two byes, Wild Card through to the Super Bowl.',
    sheets: '14 teams',
  },
  {
    slug: 'march-madness-bracket',
    label: 'March Madness',
    blurb: 'Four regions of sixteen meeting in the middle, First Round to the national title.',
    sheets: '64 teams',
  },
  {
    slug: 'college-football-playoff-bracket',
    label: 'College Football Playoff',
    blurb: 'Twelve teams, the four byes, campus first round through to the title game.',
    sheets: '12 teams',
  },
];
